import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

import ProductionUnitApi from "../../services/ProductionUnitApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import FamilyApi from "../../services/FamilyApi";
import FamilyFmeaApi from "../../services/FamilyFmeaApi";
import FmeaTypeEnum from "../../enum/FmeaTypeEnum";
import FmeaStatusEnum from "../../enum/FmeaStatusEnum";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const FmeaTypeLabels: Record<FmeaTypeEnum, string> = {
  [FmeaTypeEnum.AIAG_2008]: "AIAG 2008",
  [FmeaTypeEnum.AIAG_VDA_2019]: "AIAG/VDA 2019",
};

const fmeaTypeOptions = Object.values(FmeaTypeEnum)
  .filter((v) => typeof v === "number")
  .map((value) => ({
    label: FmeaTypeLabels[value as FmeaTypeEnum],
    value: value as FmeaTypeEnum,
  }));

const statusOptions = Object.entries(FmeaStatusEnum)
  .filter(([key]) => !isNaN(Number(key)))
  .map(([key, label]) => ({ label: String(label), value: Number(key) }));

const CreateFamilyFmeaForm: React.FC = () => {
  const { plantId } = useAuth();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translate } = useTranslation("form");

  const translatedStatusOptions = useMemo(() => statusOptions.map((opt) => ({
    ...opt,
    label: translate(opt.label.toLowerCase()),
  })), [translate]);

  // Refs for auto-focus if needed (though DS Input handles most logic)
  const areaRef = useRef<any>(null);
  const lineRef = useRef<any>(null);
  const sublineRef = useRef<any>(null);
  const familyRef = useRef<any>(null);

  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);
  const [areas, setAreas] = useState<{ id: string; name: string }[]>([]);
  const [lines, setLines] = useState<{ id: string; name: string }[]>([]);
  const [sublines, setSublines] = useState<{ id: string; name: string }[]>([]);
  const [families, setFamilies] = useState<{ id: string; name: string }[]>([]);

  const [selectedUnitId, setSelectedUnitId] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedLineId, setSelectedLineId] = useState<string>("");
  const [selectedSublineId, setSelectedSublineId] = useState<string>("");
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [typeFmea, setTypeFmea] = useState<FmeaTypeEnum>(FmeaTypeEnum.AIAG_VDA_2019);
  const [responsible, setResponsible] = useState<string>("");
  const [status, setStatus] = useState<FmeaStatusEnum>(FmeaStatusEnum.Active);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    ProductionUnitApi.GetProductUnitByPlant(plantId)
      .then((resp) => setUnits(resp.data))
      .catch((err) => displayToast("error", translate("errorLoading"), err.message));
  }, [plantId, displayToast, translate]);

  useEffect(() => {
    if (!selectedUnitId) {
      setAreas([]);
      setSelectedAreaId("");
      return;
    }
    AreaApi.GetAreaByProductionUnit(selectedUnitId)
      .then((resp) => {
        setAreas(resp.data);
        // Cascading reset
        setSelectedAreaId("");
      })
      .catch((err) => displayToast("error", translate("errorLoading"), err.message));
  }, [selectedUnitId, displayToast, translate]);

  useEffect(() => {
    if (!selectedAreaId) {
      setLines([]);
      setSelectedLineId("");
      return;
    }
    LineApi.GetLinesByArea(selectedAreaId)
      .then((resp) => {
        setLines(resp.data);
        setSelectedLineId("");
      })
      .catch((err) => displayToast("error", translate("errorLoading"), err.message));
  }, [selectedAreaId, displayToast, translate]);

  useEffect(() => {
    if (!selectedLineId) {
      setSublines([]);
      setSelectedSublineId("");
      setFamilies([]);
      setSelectedFamilyId("");
      return;
    }

    // Fetch Sublines
    SublineApi.GetSublineByLine(selectedLineId)
      .then((resp) => setSublines(resp.data))
      .catch((err) => displayToast("error", translate("errorLoading"), err.message));

    // Fetch Families
    FamilyApi.GetFamiliesByLine(selectedLineId)
      .then((resp) => setFamilies(resp.data || []))
      .catch((err) => displayToast("error", translate("errorLoading"), err.message));

    setSelectedSublineId("");
    setSelectedFamilyId("");
  }, [selectedLineId, displayToast, translate]);

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    if (!title.trim() || !description.trim() || !selectedSublineId || !selectedFamilyId || !responsible.trim()) {
      displayToast("warn", translate("warning"), translate("fillInAllRequiredFields"));
      return;
    }

    try {
      const created = await FamilyFmeaApi.Create({
        title,
        description,
        sublineId: selectedSublineId,
        familyId: selectedFamilyId,
        fmeaType: typeFmea,
        responsible,
        status,
      });

      displayToast("success", translate("Success"), translate("FmeaCreated"), false, 3000);

      if (typeFmea === FmeaTypeEnum.AIAG_VDA_2019) {
        const createdId = created?.id || created?.data?.id || created?.fmeaId || created?.familyFmeaId;
        if (createdId) {
          navigate(`/fmeas/${createdId}/steps/1`);
          return;
        }
      }
      navigate("/family-fmeas");
    } catch (err: any) {
      displayToast("error", translate("FmeaNotCreated"), err.message || err, false, 3000);
    }
  };

  return (
    <>
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/family-fmeas")}
      />
      <FormCard
        title={translate("registerFamilyFmea")}
        icon="family_history"
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitButtonLabel={translate("save")}
        cancelButtonLabel={translate("Cancel")}
        numColumns={12}
      >
        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="text"
            label={translate("title")}
            value={title}
            required
            placeholder={`${translate("title")} *`}
            maxLength={200}
            onChange={(val: any) => setTitle(val)}
          />
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="textArea"
            label={translate("description")}
            value={description}
            required
            rows={3}
            placeholder={`${translate("description")} *`}
            maxLength={1000}
            onChange={(val: any) => setDescription(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("fmeaType")}
            value={typeFmea}
            required
            options={fmeaTypeOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("fmeaType")} *`}
            onChange={(val: any) => setTypeFmea(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("responsible")}
            value={responsible}
            required
            placeholder={`${translate("responsible")} *`}
            onChange={(val: any) => setResponsible(val)}
          />
        </div>

        {/* Hierarchy */}
        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("productionUnit")}
            value={selectedUnitId}
            required
            options={units.map(u => ({ label: u.name, value: u.id }))}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("selectUnit")} *`}
            onChange={(val: any) => setSelectedUnitId(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={areaRef}
            type="select"
            label={translate("area")}
            value={selectedAreaId}
            required
            disabled={!areas.length}
            options={areas.map(a => ({ label: a.name, value: a.id }))}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("selectArea")} *`}
            onChange={(val: any) => setSelectedAreaId(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={lineRef}
            type="select"
            label={translate("line")}
            value={selectedLineId}
            required
            disabled={!lines.length}
            options={lines.map(l => ({ label: l.name, value: l.id }))}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("selectLine")} *`}
            onChange={(val: any) => setSelectedLineId(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={sublineRef}
            type="select"
            label={translate("subline")}
            value={selectedSublineId}
            required
            disabled={!sublines.length}
            options={sublines.map(s => ({ label: s.name, value: s.id }))}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("selectSubline")} *`}
            onChange={(val: any) => setSelectedSublineId(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={familyRef}
            type="select"
            label={translate("family")}
            value={selectedFamilyId}
            required
            disabled={!families.length}
            options={families.map(f => ({ label: f.name, value: f.id }))}
            optionLabel="label"
            optionValue="value"
            placeholder={`${translate("selectFamily")} *`}
            onChange={(val: any) => setSelectedFamilyId(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("status")}
            value={status}
            disabled
            options={translatedStatusOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(val: any) => setStatus(val)}
          />
        </div>
      </FormCard>
    </>
  );
};

export default CreateFamilyFmeaForm;